import { Injectable } from '@angular/core';

import { BrazilRegulatoryData, RegulatoryDataResponse } from '@bpos/v1/my-bets';
import { ApiService } from '@frontend/sports/common/api-utils';
import { MyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { ExpiringCache } from '@frontend/sports/common/core/utils/expiring-cache';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { BetslipListType, MyBetsFilteredResponse, MyBetsListSlimResponse, MyBetsSingleResponse } from '@frontend/sports/types/models/my-bets';
import { Observable, catchError, firstValueFrom, map, of } from 'rxjs';

import { myBetsListSlimErrorResponse } from '../../my-bets-base/models/my-bets-response';

export interface IMyBetsListRequest {
    typeFilter?: BetslipListType;
    index: number;
    maxItems?: number;
    pinnedBetslipIds?: string;
    eventIds: string[] | null;
    outcome?: string;
    fromDate?: string;
    toDate?: string;
    associatedAccount?: string;
}

@Injectable({ providedIn: 'root' })
export class MyBetsApiService {
    private readonly logger: SportsRemoteLogger;
    private allBetslipsCache: ExpiringCache;
    private singleBetslipCache: ExpiringCache;
    private getMyBetsCache: ExpiringCache;

    constructor(
        private apiService: ApiService,
        loggerFactory: LoggerFactory,
        private myBetsConfig: MyBetsConfig,
    ) {
        this.logger = loggerFactory.getLogger('MyBetsApiService');

        const timeout = (this.myBetsConfig.cacheTimeout || 100) / 1000;

        this.allBetslipsCache = new ExpiringCache(timeout);
        this.singleBetslipCache = new ExpiringCache(timeout);
        this.getMyBetsCache = new ExpiringCache(timeout);
    }

    getSingleBetslip({
        betslipId,
        associatedAccount,
        showAltLang = false,
        useDetails = false,
    }: {
        betslipId: string;
        associatedAccount: string;
        showAltLang?: boolean;
        useDetails?: boolean;
    }): Observable<MyBetsSingleResponse | null> {
        const source = this.apiService
            .get<MyBetsSingleResponse>('mybets/betslip', {
                betslipId,
                associatedAccount,
                showAltLang,
                useDetails,
            })
            .pipe(
                map((response) => ({ betslip: response.betslip, events: {} })),
                catchError((error) => {
                    this.logger.error(error, `Searching for Betslip with ID: ${betslipId} failed`);

                    return of(null);
                }),
            );

        return this.singleBetslipCache.getOrCreate(`mybets-single-betslip-${betslipId}-${associatedAccount}-${showAltLang}-${useDetails}`, source);
    }

    getAllBetslips(options: IMyBetsListRequest): Observable<MyBetsListSlimResponse | null> {
        const source = this.apiService.get<MyBetsListSlimResponse>('mybets/allBetslips', options).pipe(
            catchError((error) => {
                this.logger.error(error, 'Get MyBets response failed.');

                return of(null);
            }),
        );

        return this.allBetslipsCache.getOrCreate(
            `mybets-all-betslips-${options.associatedAccount}-${options.outcome}-${options.fromDate}-${options.toDate}`,
            source,
        );
    }

    getMyBets(options: IMyBetsListRequest): Observable<MyBetsFilteredResponse | null> {
        const source = this.apiService.get<MyBetsFilteredResponse>('mybets/betslips', options).pipe(
            catchError((error) => {
                this.logger.error(error, 'Get MyBets response failed.');

                return of(null);
            }),
        );

        return this.getMyBetsCache.getOrCreate(
            `mybets-get-mybets-${options.typeFilter}-${options.index}-${options.maxItems}-${options.pinnedBetslipIds}-${options.eventIds}-${options.outcome}-${options.fromDate}-${options.toDate}-${options.associatedAccount}`,
            source,
        );
    }

    fetchDeviceDetailsByBetSlipId(betSlipId: string): Observable<BrazilRegulatoryData | undefined> {
        const cacheKey = `mybets-device-detail-${betSlipId}`;
        const source$ = this.apiService.get<RegulatoryDataResponse>('mybets/regulatorydata', { betSlipId }).pipe(
            map((response) => response?.brazilRegulatoryData?.[0]),
            catchError((error) => {
                this.logger.error(error, 'Unable to retrieve device details.');
                return of(undefined);
            }),
        );

        return this.allBetslipsCache.getOrCreate(cacheKey, source$);
    }

    async getEditBetBetslips(betslipId: string, associatedAccount: Nullable<string>): Promise<MyBetsListSlimResponse> {
        return firstValueFrom(
            this.apiService
                .get<MyBetsListSlimResponse>('mybets/editBetBetslips', {
                    betslipId,
                    associatedAccount,
                })
                .pipe(
                    catchError((err) => {
                        this.logger.error(err, 'Get EditBetBetslips response failed.');

                        return of(myBetsListSlimErrorResponse());
                    }),
                    map((result) => result || myBetsListSlimErrorResponse()),
                ),
        );
    }
}
